import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Stack,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Code as CodeIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';
import BackendCallDialog from '../components/BackendCallDialog';

const StoriesInfoColumn = () => {
  const { storiesResults, storiesLastCall } = useBuild();
  const [backendCallDialogOpen, setBackendCallDialogOpen] = useState(false);

  const handleOpenBackendCallDialog = () => {
    setBackendCallDialogOpen(true);
  };

  const handleCloseBackendCallDialog = () => {
    setBackendCallDialogOpen(false);
  };

  const renderInfo = () => {
    // Show error message if there's an error in storiesResults
    if (storiesResults?.error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Query Error
          </Typography>
          <Typography variant="body2">
            {storiesResults.error}
          </Typography>
        </Alert>
      );
    }

    if (!storiesResults) {
      return (
        <Typography variant="body2" color="text.secondary">
          No query executed yet.
        </Typography>
      );
    }

    return (
      <Stack spacing={2}>
        {/* Backend Call Button */}
        <Button
          variant="outlined"
          startIcon={<CodeIcon />}
          onClick={handleOpenBackendCallDialog}
          fullWidth
          disabled={!storiesLastCall}
        >
          Backend Call
        </Button>

        {/* Errors Section */}
        {storiesResults.errors && Array.isArray(storiesResults.errors) && storiesResults.errors.length > 0 && (
          <Stack spacing={1}>
            {storiesResults.errors.map((error, idx) => (
              <Alert key={idx} severity="warning">
                <Typography variant="body2">
                  {error}
                </Typography>
              </Alert>
            ))}
          </Stack>
        )}

        {/* Stories Count */}
        {storiesResults.stories && Array.isArray(storiesResults.stories) && (
          <Alert severity="success">
            <Typography variant="body2">
              Stories Generated: {storiesResults.stories.length}
            </Typography>
          </Alert>
        )}

        {/* Stories Count by Type */}
        {storiesResults.stories && Array.isArray(storiesResults.stories) && storiesResults.stories.length > 0 && (() => {
          const typeCounts = {};
          storiesResults.stories.forEach(story => {
            const storyType = story.type || 'Unknown';
            typeCounts[storyType] = (typeCounts[storyType] || 0) + 1;
          });
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Stories by Type
              </Typography>
              <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxWidth: '100%', overflow: 'hidden' }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', padding: '4px 8px', width: '70%', wordBreak: 'break-word' }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', padding: '4px 8px', width: '30%' }}>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(typeCounts).map(([type, count]) => (
                      <TableRow key={type} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                        <TableCell sx={{ fontSize: '0.75rem', padding: '4px 8px', wordBreak: 'break-word' }}>{type}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', padding: '4px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })()}

        {/* Timing Information */}
        {(storiesResults.metrics?.took_backend !== undefined || storiesResults.took_frontend !== undefined) && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Latency
            </Typography>
            <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxWidth: '100%', overflow: 'hidden' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', padding: '4px 8px', width: '70%', wordBreak: 'break-word' }}>Side</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', padding: '4px 8px', width: '30%' }}>ms</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storiesResults.metrics?.took_backend !== undefined && (
                    <TableRow sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                      <TableCell sx={{ fontSize: '0.75rem', padding: '4px 8px', wordBreak: 'break-word' }}>Backend</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', padding: '4px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{storiesResults.metrics.took_backend}</TableCell>
                    </TableRow>
                  )}
                  {storiesResults.took_frontend !== undefined && (
                    <TableRow sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                      <TableCell sx={{ fontSize: '0.75rem', padding: '4px 8px', wordBreak: 'break-word' }}>Frontend</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', padding: '4px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{storiesResults.took_frontend}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Reported Metrics Section */}
        {storiesResults.metrics && Object.keys(storiesResults.metrics).length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Reported Metrics
            </Typography>
            <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxWidth: '100%', overflow: 'hidden' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', padding: '4px 8px', width: '60%', wordBreak: 'break-word' }}>Key</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', padding: '4px 8px', width: '40%' }}>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(storiesResults.metrics).map(([key, value]) => (
                    <TableRow key={key} sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                      <TableCell sx={{ fontSize: '0.75rem', padding: '4px 8px', wordBreak: 'break-word', overflow: 'hidden', textOverflow: 'ellipsis' }} title={key}>{key}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem', padding: '4px 8px', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {typeof value === 'number' ? Math.round(value) : String(value)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        Information
      </Typography>

      <Box sx={{ mt: 2, flex: 1, overflow: 'auto' }}>
        {renderInfo()}
      </Box>

      {/* Backend Call Dialog */}
      <BackendCallDialog
        open={backendCallDialogOpen}
        onClose={handleCloseBackendCallDialog}
        apiCall={storiesLastCall}
        maxWidth="md"
      />
    </Paper>
  );
};

export default StoriesInfoColumn;

